import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function Railway() {
  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Full Regulatory Compliance',
      description: 'Complete adherence to U.S. and international railway transportation regulations and safety standards.',
    },
    {
      icon: 'ri-train-line',
      title: 'Professional Management',
      description: 'Experienced logistics team managing all aspects of railway petroleum transportation.',
    },
    {
      icon: 'ri-route-line',
      title: 'Extensive Network',
      description: 'Access to comprehensive railway networks for efficient nationwide distribution.',
    },
    {
      icon: 'ri-time-line',
      title: 'Reliable Scheduling',
      description: 'Dependable transit times with coordinated scheduling for timely deliveries.',
    },
    {
      icon: 'ri-file-shield-line',
      title: 'Safety Standards',
      description: 'Highest industry safety standards for petroleum product rail transport.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'End-to-End Service',
      description: 'Complete logistics support from loading to final destination delivery.',
    },
  ];

  const advantages = [
    'Cost-effective long-distance transport',
    'High-capacity bulk shipping',
    'Reduced environmental impact',
    'Weather-independent operations',
    'Secure and monitored transport',
    'Flexible routing options',
    'Integration with other transport modes',
    'Comprehensive insurance coverage',
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 20, 60, 0.7), rgba(0, 20, 60, 0.7)), url('https://public.readdy.ai/ai/img_res/405a7ec4fccc17ed7324fdc3902ae28d.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">Railway Shipping Services</h1>
            <p className="text-xl text-gray-200 max-w-3xl leading-relaxed">
              Professional railway transportation ensuring full compliance with U.S. and international regulations for safe petroleum delivery
            </p>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20railway%20tank%20cars%20at%20industrial%20rail%20terminal%20facility%2C%20authentic%20documentary%20photography%20of%20genuine%20petroleum%20railway%20shipping%20with%20specialized%20tank%20wagons%20showing%20professional%20operations%20and%20modern%20infrastructure%2C%20professional%20photojournalism%20of%20actual%20rail%20transport%20of%20oil%20products%20showing%20real%20safety%20compliance%20and%20working%20logistics%2C%20realistic%20commercial%20photograph%20of%20railway%20petroleum%20transport%20at%20terminal&width=600&height=400&seq=railway-terminal-001&orientation=landscape"
                  alt="Railway Terminal"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20railway%20tank%20cars%20being%20loaded%20with%20petroleum%20products%20at%20industrial%20facility%2C%20authentic%20documentary%20photography%20of%20genuine%20petroleum%20railway%20shipping%20loading%20operations%20with%20specialized%20equipment%20and%20safety%20protocols%2C%20professional%20photojournalism%20of%20actual%20rail%20transport%20loading%20showing%20real%20safety%20compliance%20and%20working%20logistics%20infrastructure%2C%20realistic%20commercial%20photograph&width=600&height=400&seq=railway-loading-001&orientation=landscape"
                  alt="Loading Operations"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20railway%20petroleum%20tank%20cars%20at%20industrial%20facility%2C%20authentic%20documentary%20photography%20of%20actual%20oil%20transport%20rail%20wagons%20with%20company%20branding%2C%20professional%20photojournalism%20showing%20genuine%20railway%20shipping%20operations%20with%20real%20tank%20cars%20and%20rail%20infrastructure%2C%20realistic%20commercial%20photograph%20of%20working%20petroleum%20railway%20transport&width=600&height=400&seq=railway-fleet-001&orientation=landscape"
                  alt="Railway Fleet"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Reliable Railway Transportation</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  We offer professionally managed railway transportation for petroleum products, ensuring full compliance with all applicable United States and international regulations. Our railway shipping services are designed to meet the highest industry standards, guaranteeing the safe, reliable, and efficient movement of your materials via rail.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Railway transport offers significant advantages for long-distance petroleum product shipping, including cost-effectiveness, high capacity, and reduced environmental impact compared to other transportation modes. Our experienced team manages every aspect of the shipping process, from loading and documentation to tracking and final delivery.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  With access to extensive railway networks and partnerships with major rail carriers, we provide flexible routing options and reliable scheduling to meet your specific delivery requirements. Our commitment to safety and compliance ensures your petroleum products arrive securely and on time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Railway Service Features</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive rail transportation solutions with professional management and full compliance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Railway Transport Advantages</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Railway transportation offers numerous benefits for petroleum product shipping, combining efficiency, safety, and environmental responsibility. Our rail services provide a reliable alternative for long-distance bulk transport.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {advantages.map((advantage, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                      <span className="text-gray-700">{advantage}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Compliance & Safety</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-government-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">U.S. Regulations</h4>
                      <p className="text-gray-600">Full compliance with DOT and FRA requirements</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">International Standards</h4>
                      <p className="text-gray-600">Adherence to global railway safety protocols</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-list-3-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Documentation</h4>
                      <p className="text-gray-600">Complete shipping documentation and manifests</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-eye-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Tracking & Monitoring</h4>
                      <p className="text-gray-600">Real-time shipment tracking and status updates</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Railway Shipping Process</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Booking & Planning</h3>
                <p className="text-gray-600">Route planning and capacity reservation</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Loading & Documentation</h3>
                <p className="text-gray-600">Safe loading with complete paperwork</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transit & Tracking</h3>
                <p className="text-gray-600">Monitored transport with real-time updates</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Delivery & Unloading</h3>
                <p className="text-gray-600">Safe delivery to final destination</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Request Railway Shipping</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Contact us to discuss your petroleum railway transportation needs
            </p>
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}